// NOTE (Feb 2026): The conversational chat below is currently a scripted/mock
// flow (predefined messages, simulated team replies). OpenAI is wired only to
// the Production module (`/app/frontend/src/components/gestor/ProductionModule.js`)
// for AI image generation. A future iteration will connect this chat to OpenAI
// for natural-language brief intake — see /api/chat (TODO) when that ships.

import { useState, useRef, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { API, xhrJson, authHeader } from '@/lib/api';
import { ProjectStatusBlock, ChatTimer } from '@/components/shared/CountdownTimer';
import QueueStatusPill from '@/components/client/QueueStatusPill';
import { VideoPlayer } from '@/components/shared/VideoPlayer';
import { LOGO_URL } from '@/data/mockData';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import RecordingBar from '@/components/shared/RecordingBar';
import {
  Image, FileText, Film, Music,
  Download, CheckCircle2, Plus, X, Mic, PlayCircle, Loader2
} from 'lucide-react';

function RocketIcon({ className }) {
  return (
    <svg viewBox="0 0 600 600" className={className} xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0,600) scale(0.1,-0.1)" fill="currentColor" stroke="none">
        <path d="M2941 4945 c-53 -48 -119 -149 -202 -310 -91 -176 -163 -364 -249 -645 -156 -513 -274 -797 -481 -1152 -144 -247 -174 -325 -233 -603 -62 -295 -129 -830 -112 -898 7 -31 34 -41 72 -27 16 6 185 169 384 371 195 198 361 360 367 359 7 0 28 -31 47 -68 19 -38 47 -83 61 -100 l27 -32 372 0 c314 0 375 2 393 15 12 8 42 53 68 100 25 47 51 85 58 85 7 1 167 -156 356 -347 189 -191 355 -356 370 -365 39 -27 79 -23 93 8 21 46 -37 548 -97 843 -63 312 -106 430 -238 652 -179 302 -313 608 -437 999 -169 531 -181 564 -297 793 -69 136 -165 288 -204 324 -44 40 -72 39 -118 -2z"/>
        <path d="M2763 1638 c15 -188 133 -458 212 -488 37 -14 64 3 112 69 70 95 132 274 148 424 l7 67 -242 0 -243 0 6 -72z"/>
      </g>
    </svg>
  );
}

function FileIcon({ type }) {
  switch (type) {
    case 'image': return <Image className="w-4 h-4" />;
    case 'video': return <Film className="w-4 h-4" />;
    case 'audio': return <Music className="w-4 h-4" />;
    default: return <FileText className="w-4 h-4" />;
  }
}

export default function ChatArea() {
  const { activeClientProjectId, setActiveClientProjectId, getProject, getBrand, addMessage, updateProject, updateProjectStatus, createProject, markProjectRead, isLoggedIn, uploadProjectFile } = useApp();
  const { session, profile, refreshProfile } = useAuth();
  const project = getProject(activeClientProjectId);
  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [videoSizes, setVideoSizes] = useState([]);
  const [selectedSize, setSelectedSize] = useState('short');
  const [startBusy, setStartBusy] = useState(false);
  const [startError, setStartError] = useState(null);

  // ── Production AI Brief Assistant state (logged-in clients, status==='new')
  // Brief conversation is canonical for new projects; it gates the
  // "Iniciar producción" button until readiness_status === 'ready'.
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [briefError, setBriefError] = useState(null);
  const briefMode = isLoggedIn && project?.status === 'new';

  // Load (or seed) the brief whenever a logged-in client opens a new project.
  useEffect(() => {
    if (!briefMode || !session?.access_token || !project?.id) {
      setBrief(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { ok, data } = await xhrJson(
        'GET', `${API}/api/briefs/${project.id}`, null, authHeader(session),
      );
      if (!cancelled && ok) setBrief(data.brief);
    })();
    return () => { cancelled = true; };
  }, [briefMode, project?.id, session?.access_token]);

  const briefMessages = (brief?.conversation || []).map((m, i) => ({
    id: `brief-${i}`,
    type: m.role === 'user' ? 'user' : 'system',
    sender: m.role === 'user' ? 'user' : 'system',
    content: m.content || '',
    timestamp: m.ts,
    actions: (m.suggestions || []).map(s => ({ label: s, value: s })),
    files: (m.files || []).map(a => ({
      name: a.name, type: a.type || 'document',
      size: a.size ? `${Math.round(a.size / 1024)} KB` : '',
      url: a.url,
    })),
  }));

  const briefReady = brief?.readiness_status === 'ready'
    || brief?.readiness_status === 'confirmed';

  // Load size grid for the start-production picker
  useEffect(() => {
    if (!session?.access_token) return;
    let cancelled = false;
    (async () => {
      const { ok, data } = await xhrJson(
        'GET', `${API}/api/credits/video-sizes`, null, authHeader(session),
      );
      if (!cancelled && ok) setVideoSizes(data.video_sizes || []);
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  const sizeCfg = videoSizes.find((s) => s.id === selectedSize);
  const credits = profile?.credits ?? 0;
  const canAfford = !sizeCfg || credits >= (sizeCfg.credits_required || 0);

  const voice = useVoiceRecorder({
    language: 'es',
    prompt: 'Brief de proyecto de video para Ruido.ai: marca, objetivo, urgencia, referencias.',
    maxSeconds: 300,
    onTranscribed: (text) => {
      setInput(prev => (prev ? `${prev} ${text}` : text));
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.style.height = 'auto';
          el.style.height = Math.min(el.scrollHeight, 280) + 'px';
        }
      });
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [project?.messages?.length]);

  // Mark project as read when opened
  useEffect(() => {
    if (project?.hasNewUpdate) {
      markProjectRead(project.id);
    }
  }, [activeClientProjectId]);

  if (!project) {
    const handleNewProject = () => {
      const newId = createProject('New Video Project');
      setActiveClientProjectId(newId);
    };

    return (
      <div className="flex flex-col h-[100dvh] md:h-full relative" data-testid="chat-area-empty">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 px-4 md:px-6 pt-4 pointer-events-none">
          <div className="flex items-center justify-center pl-12 md:pl-0 pointer-events-auto">
            <div className="glass-pill px-5 py-2.5 rounded-full">
              <h2 className="text-[13px] font-medium text-gray-900 font-['Outfit']">New Project</h2>
            </div>
          </div>
        </div>
        {/* Welcome content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="text-center max-w-sm animate-fade-in">
            <div className="w-14 h-14 rounded-full overflow-hidden mx-auto mb-5 opacity-40">
              <img src={LOGO_URL} alt="Ruido.ai" className="w-full h-full object-cover scale-[1.15]" />
            </div>
            <h2 className="text-lg font-light text-gray-900 font-['Outfit'] mb-2">Tell us about the video you want to create</h2>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">Describe your vision, upload references, or start with a quick action below.</p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button
                data-testid="new-project-start"
                onClick={handleNewProject}
                className="px-4 py-2 rounded-full text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-all active:scale-[0.97]"
              >
                Start project
              </button>
              <button
                data-testid="new-project-examples"
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 rounded-full text-sm bg-white/40 border border-gray-200/50 text-gray-600 hover:text-gray-900 hover:bg-white/60 transition-all active:scale-[0.97]"
              >
                See examples
              </button>
              <a
                href="/credits"
                data-testid="new-project-credits"
                className="px-4 py-2 rounded-full text-sm bg-white/40 border border-gray-200/50 text-gray-600 hover:text-gray-900 hover:bg-white/60 transition-all active:scale-[0.97]"
              >
                View credits
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Upload a single file to /api/briefs/{pid}/upload. Returns the asset
  // descriptor or null on failure.
  const uploadBriefAsset = async (raw) => {
    try {
      const form = new FormData();
      form.append('file', raw);
      const res = await fetch(`${API}/api/briefs/${project.id}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: form,
      });
      if (!res.ok) throw new Error(`upload ${res.status}`);
      const data = await res.json();
      return data?.asset || null;
    } catch (e) {
      console.warn('Brief asset upload failed:', e);
      return null;
    }
  };

  const handleSend = async () => {
    if (!input.trim() && pendingFiles.length === 0) return;

    // Production AI Brief Assistant — replaces the scripted onboarding flow
    // for any logged-in client whose project is still in `new` state.
    if (briefMode) {
      const userText = input.trim();

      // 1. Upload pending files in parallel. Track success/failure individually
      // so partial failures surface a clear notice instead of dropping silently.
      let uploadedAssets = [];
      let failedCount = 0;
      const filesToUpload = pendingFiles.filter(f => f.raw);
      if (filesToUpload.length > 0) {
        setBriefBusy(true);
        setBriefError(null);
        const results = await Promise.all(filesToUpload.map(pf => uploadBriefAsset(pf.raw)));
        uploadedAssets = results.filter(Boolean);
        failedCount = results.length - uploadedAssets.length;
        if (uploadedAssets.length === 0 && filesToUpload.length > 0) {
          setBriefBusy(false);
          setBriefError("We couldn't upload your files. Try again.");
          return;
        }
      }

      // 2. Compose the chat message. We only keep the free-text the user
      // actually typed — attachments are carried separately in
      // `uploaded_assets` and rendered as real thumbnails in the bubble,
      // so there is no need to paste a "(Attached: …)" sentence into
      // the content.
      const composedMessage = userText
        || (uploadedAssets.length > 0 ? ' ' : '');  // blank space keeps the turn valid

      if (!composedMessage && uploadedAssets.length === 0) {
        setBriefBusy(false);
        return;
      }

      setInput('');
      setPendingFiles([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';

      // Optimistic local turn so the UI feels instant.
      setBrief(prev => prev
        ? { ...prev,
            uploaded_assets: [...(prev.uploaded_assets || []), ...uploadedAssets],
            conversation: [...(prev.conversation || []),
                           { role: 'user', content: composedMessage, ts: new Date().toISOString(),
                             files: uploadedAssets }] }
        : prev);

      setBriefBusy(true);
      // Surface partial failures ALONGSIDE the still-sent message.
      if (failedCount > 0) {
        setBriefError(`Some files failed to upload (${failedCount}/${filesToUpload.length}). Successful files were sent.`);
      } else {
        setBriefError(null);
      }
      const { ok, status, data } = await xhrJson(
        'POST', `${API}/api/briefs/${project.id}/chat`,
        { message: composedMessage, uploaded_assets: uploadedAssets },
        authHeader(session),
      );
      setBriefBusy(false);
      if (!ok) {
        const detail = typeof data?.detail === 'string'
          ? data.detail
          : status === 401 ? 'Please sign in again to continue.'
          : "We couldn't process your message. Try again.";
        setBriefError(detail);
        return;
      }
      setBrief(data.brief);
      return;
    }

    const msg = { type: 'user', content: input.trim(), sender: 'user' };

    // If logged in and we have raw files, upload them to Supabase Storage first
    let uploadedFiles = pendingFiles.map(({ raw, projectId, ...rest }) => rest);
    if (isLoggedIn && pendingFiles.some(f => f.raw)) {
      try {
        const results = await Promise.all(pendingFiles.map(async (pf) => {
          if (!pf.raw) return pf;
          const { file } = await uploadProjectFile(project.id, pf.raw);
          return {
            id: file.id,
            name: file.name,
            type: pf.type,
            size: file.size_label || pf.size,
            url: file.url,
            storage_path: file.storage_path,
            bucket: file.bucket,
          };
        }));
        uploadedFiles = results.map(({ raw, projectId, ...rest }) => rest);
      } catch (e) {
        console.warn('File upload failed, attaching local metadata only:', e);
      }
    }
    if (uploadedFiles.length > 0) {
      msg.files = uploadedFiles;
    }
    addMessage(project.id, msg);
    setInput('');
    setPendingFiles([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    if (project.status === 'new') {
      setTimeout(() => {
        addMessage(project.id, {
          type: 'system',
          content: "Got it! You can keep adding details, upload reference files, or select a brand. When you're ready, hit Confirm & Start Project below.",
          sender: 'system'
        });
      }, 800);
    }
  };

  // Quick-reply for AI brief suggestion chips.
  const handleBriefSuggestion = async (text) => {
    if (briefBusy || !briefMode) return;
    setInput('');
    setBrief(prev => prev
      ? { ...prev,
          conversation: [...(prev.conversation || []),
                         { role: 'user', content: text, ts: new Date().toISOString() }] }
      : prev);
    setBriefBusy(true);
    setBriefError(null);
    const { ok, data } = await xhrJson(
      'POST', `${API}/api/briefs/${project.id}/chat`,
      { message: text },
      authHeader(session),
    );
    setBriefBusy(false);
    if (!ok) {
      setBriefError('No se pudo procesar la sugerencia. Intenta otra.');
      return;
    }
    setBrief(data.brief);
  };

  const handleConfirmProject = async () => {
    if (!project) return;
    if (!isLoggedIn || !session?.access_token) {
      // Anonymous fallback — keep old optimistic flow.
      updateProjectStatus(project.id, 'received');
      setTimeout(() => {
        addMessage(project.id, {
          type: 'system',
          content: "Our team will review your brief and start production shortly. We'll keep you updated right here.",
          sender: 'system'
        });
      }, 500);
      return;
    }
    setStartBusy(true);
    setStartError(null);
    const { ok, status, data } = await xhrJson(
      'POST',
      `${API}/api/projects/${project.id}/consume-credits`,
      { project_size: selectedSize },
      authHeader(session),
    );
    setStartBusy(false);
    if (!ok) {
      const msg = typeof data?.detail === 'string'
        ? data.detail
        : status === 402
          ? 'No tienes créditos suficientes para iniciar este proyecto.'
          : 'No se pudo iniciar la producción.';
      setStartError(msg);
      return;
    }
    const charged = data.project?.credits_consumed ?? sizeCfg?.credits_required ?? 1;
    const range = sizeCfg ? `${sizeCfg.min_seconds}–${sizeCfg.max_seconds}s` : '';
    updateProject(project.id, {
      status: 'in_production',
      creditsConsumed: charged,
      project_size: selectedSize,
      requested_duration_range: data.project?.requested_duration_range || range,
      deliveryDeadline: data.delivery_due_at,
    });
    updateProjectStatus(project.id, 'in_production');
    addMessage(project.id, {
      type: 'system',
      content: `Producción iniciada · ${sizeCfg?.label || ''} (${range}) · −${charged} crédito(s). Recibirás tu video cuando el equipo lo entregue.`,
      sender: 'system',
    });
    refreshProfile?.();
  };

  const handleQuickAction = (action) => {
    addMessage(project.id, { type: 'user', content: `I'd like a ${action.value} video.`, sender: 'user' });
    updateProject(project.id, { videoType: action.value });
    setTimeout(() => {
      addMessage(project.id, {
        type: 'system',
        content: 'Great choice! How long should the video be?',
        sender: 'system',
        actions: [
          { label: '15s', value: '15 seconds' },
          { label: '30s', value: '30 seconds' },
          { label: '60s', value: '60 seconds' },
          { label: '90s', value: '90 seconds' },
          { label: '2min', value: '2 minutes' }
        ]
      });
    }, 700);
  };

  const handleDurationAction = (action) => {
    addMessage(project.id, { type: 'user', content: `${action.value} please.`, sender: 'user' });
    updateProject(project.id, { duration: action.value });
    setTimeout(() => {
      addMessage(project.id, {
        type: 'system',
        content: 'Perfect! You can now select a saved brand, upload files, or add any additional instructions. Hit Confirm when ready.',
        sender: 'system'
      });
    }, 700);
  };

  const handleSelectBrand = () => {};

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getFileType = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newFiles = files.map(f => ({
      name: f.name,
      type: getFileType(f),
      size: formatFileSize(f.size),
      raw: f,
      projectId: project?.id,
    }));
    setPendingFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const canSend = input.trim().length > 0 || pendingFiles.length > 0;

  const isActionDuration = (actions) => actions?.some(a => a.value.includes('seconds') || a.value.includes('minutes'));

  return (
    <div className="flex flex-col h-[100dvh] md:h-full relative" data-testid="chat-area">
      {/* Floating glass header */}
      <div className="absolute top-0 left-0 right-0 z-10 px-4 md:px-6 pt-4 pointer-events-none">
        <div className="flex items-center justify-center pl-12 md:pl-0 pointer-events-auto gap-2.5">
          <div className="glass-pill px-5 py-2.5 rounded-full">
            <h2 className="text-[13px] font-medium text-gray-900 font-['Outfit'] truncate max-w-[140px] sm:max-w-[200px] md:max-w-[300px]" data-testid="project-name">
              {project.name}
            </h2>
          </div>
          {project.status !== 'new' && (
            <ProjectStatusBlock status={project.status} deadline={project.deliveryDeadline} />
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 pt-20 pb-8 space-y-6">
          {(briefMode ? briefMessages : project.messages).map((msg, idx) => (
            <div key={msg.id} className="animate-slide-up" style={{ animationDelay: `${Math.min(idx * 40, 200)}ms` }}>

              {/* Status update */}
              {msg.type === 'status_update' && (
                <div className="flex justify-center py-2">
                  <span className="text-xs text-gray-400">{msg.content}</span>
                </div>
              )}

              {/* User message */}
              {msg.type === 'user' && (
                <div className="flex gap-3 flex-row-reverse">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5 text-[10px] font-bold text-gray-400">
                    Y
                  </div>
                  <div className="max-w-[85%]">
                    {msg.content && msg.content.trim() && (
                      <p className="text-sm text-gray-800 leading-relaxed">{msg.content}</p>
                    )}
                    {msg.files && msg.files.length > 0 && (
                      <div data-testid={`user-msg-attachments-${idx}`} className={`mt-2 ${msg.files.some(f => f.type === 'image' || f.type === 'video') ? 'grid grid-cols-2 sm:grid-cols-3 gap-2' : 'space-y-1'}`}>
                        {msg.files.map((f, i) => {
                          const isImg = f.type === 'image' && f.url;
                          const isVid = f.type === 'video' && f.url;
                          if (isImg) {
                            return (
                              <a
                                key={i}
                                href={f.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`user-msg-image-${idx}-${i}`}
                                className="group block rounded-xl overflow-hidden border border-gray-200 bg-white hover:border-gray-300 transition-colors"
                              >
                                <div className="aspect-square bg-gray-100">
                                  <img
                                    src={f.url}
                                    alt={f.name}
                                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                                    loading="lazy"
                                  />
                                </div>
                                <div className="px-2 py-1.5 text-[10px] text-gray-500 truncate bg-white">
                                  {f.name}
                                </div>
                              </a>
                            );
                          }
                          if (isVid) {
                            return (
                              <a
                                key={i}
                                href={f.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`user-msg-video-${idx}-${i}`}
                                className="block rounded-xl overflow-hidden border border-gray-200 bg-black"
                              >
                                <video
                                  src={f.url}
                                  preload="metadata"
                                  className="w-full aspect-square object-contain bg-black"
                                  muted
                                />
                                <div className="px-2 py-1.5 text-[10px] text-gray-500 truncate bg-white">
                                  {f.name}
                                </div>
                              </a>
                            );
                          }
                          return (
                            <a
                              key={i}
                              href={f.url || '#'}
                              target={f.url ? '_blank' : undefined}
                              rel={f.url ? 'noopener noreferrer' : undefined}
                              data-testid={`user-msg-file-${idx}-${i}`}
                              className="flex items-center gap-2 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              <FileIcon type={f.type} />
                              <span className="truncate">{f.name}</span>
                              <span className="text-gray-300 ml-auto shrink-0">{f.size}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* System message */}
              {msg.type === 'system' && (
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 mt-0.5">
                    <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
                  </div>
                  <div className="max-w-[90%]">
                    <p className="text-sm text-gray-600 leading-relaxed">{msg.content}</p>
                    {msg.actions && msg.actions.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {msg.actions.map((action, i) => (
                          <button
                            key={i}
                            data-testid={`action-btn-${action.value.replace(/\s+/g, '-').toLowerCase()}`}
                            onClick={() => briefMode
                              ? handleBriefSuggestion(action.value)
                              : isActionDuration(msg.actions)
                                ? handleDurationAction(action)
                                : handleQuickAction(action)}
                            disabled={briefBusy}
                            className="px-4 py-2 bg-white/50 border border-white/30 hover:border-gray-300/50 rounded-lg text-sm text-gray-600 hover:text-gray-900 transition-all active:scale-[0.97] disabled:opacity-50"
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Team message */}
              {msg.type === 'team' && (
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5 text-[10px] font-bold text-gray-500">
                    {msg.sender.charAt(0)}
                  </div>
                  <div className="max-w-[90%]">
                    <span className="text-[11px] text-gray-400 mb-1 block">{msg.sender}</span>
                    <p className="text-sm text-gray-600 leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              )}

              {/* Brief handoff bubble — snapshot of the Production Brief
                  inserted into the chat the moment the client starts
                  production. Visible to both client and manager. */}
              {msg.type === 'brief' && (
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 mt-0.5">
                    <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
                  </div>
                  <div data-testid={`chat-brief-message-${idx}`} className="flex-1 max-w-[90%]">
                    <span className="text-[10px] uppercase tracking-[0.12em] text-emerald-700 font-medium block mb-1">Production brief</span>
                    {msg.content && (
                      <pre className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-sans bg-emerald-50/40 border border-emerald-100 rounded-xl px-3 py-2">
                        {msg.content}
                      </pre>
                    )}
                    {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                      <div data-testid={`chat-brief-attachments-${idx}`} className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {msg.attachments.map((a, i) => {
                          const isImg = a.type === 'image' && a.url;
                          return isImg ? (
                            <a
                              key={i}
                              href={a.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`chat-brief-image-${idx}-${i}`}
                              className="block rounded-xl overflow-hidden border border-gray-200 bg-white"
                            >
                              <div className="aspect-square bg-gray-100">
                                <img src={a.url} alt={a.name} loading="lazy" className="w-full h-full object-cover" />
                              </div>
                              <div className="px-2 py-1 text-[10px] text-gray-500 truncate">
                                {a.name}{a.role ? ` · ${String(a.role).toUpperCase()}` : ''}
                              </div>
                            </a>
                          ) : (
                            <a
                              key={i}
                              href={a.url || '#'}
                              target={a.url ? '_blank' : undefined}
                              rel={a.url ? 'noopener noreferrer' : undefined}
                              data-testid={`chat-brief-file-${idx}-${i}`}
                              className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg"
                            >
                              <FileIcon type={a.type} />
                              <span className="truncate flex-1">{a.name}</span>
                              {a.role && (
                                <span className="text-[9px] uppercase text-emerald-600 shrink-0">{a.role}</span>
                              )}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Delivery message */}
              {msg.type === 'delivery' && msg.delivery && (
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 mt-0.5">
                    <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
                  </div>
                  <div className="flex-1 max-w-[90%]">
                    <p className="text-sm text-gray-600 font-medium mb-3">{msg.content}</p>
                    {(msg.delivery.videoUrl || msg.delivery.files?.[0]?.url) && (
                      <VideoPlayer
                        videoUrl={msg.delivery.videoUrl || msg.delivery.files?.[0]?.url}
                        thumbnail={msg.delivery.thumbnail}
                        fileName={msg.delivery.files?.[0]?.name}
                        fileSize={msg.delivery.files?.[0]?.size}
                        storagePath={msg.delivery.storage_path || msg.delivery.files?.[0]?.storage_path}
                        bucket={msg.delivery.bucket || msg.delivery.files?.[0]?.bucket || 'project-files'}
                        projectId={msg.delivery.projectId || project.id}
                        deliveryId={msg.delivery.deliveryId}
                        fileIndex={0}
                      />
                    )}
                    {msg.delivery.files && msg.delivery.files.length > 1 && (
                      <div className="mt-3 space-y-1">
                        {msg.delivery.files.slice(1).map((f, i) => (
                          <a
                            key={i}
                            href={f.url || '#'}
                            download={f.name}
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                            data-testid={`delivery-extra-file-${i}`}
                          >
                            <FileIcon type={f.type} />
                            <span className="truncate">{f.name}</span>
                            <span className="text-gray-300 ml-auto shrink-0">{f.size}</span>
                            <Download className="w-3.5 h-3.5 text-gray-300" />
                          </a>
                        ))}
                      </div>
                    )}
                    {(project.revisionCount || 0) < 1 && project.status !== 'delivered' && (
                    <div className="mt-3">
                      <Button
                        data-testid="request-adjustment-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => updateProjectStatus(project.id, 'revision_requested')}
                        className="bg-white/50 border-gray-200/60 text-gray-500 hover:text-gray-700 hover:bg-white/70 text-xs rounded-lg"
                      >
                        Request Adjustment
                      </Button>
                    </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Inline timer when active */}
          {(project.status === 'in_production' || project.status === 'received') && (
            <>
              <div className="flex justify-center mt-2">
                <QueueStatusPill projectId={project.id} />
              </div>
              <ChatTimer deadline={project.deliveryDeadline} />
            </>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Brief error banner — shown above the input regardless of card state. */}
      {briefMode && briefError && (
        <div className="px-4 pt-1 shrink-0">
          <div className="max-w-3xl mx-auto">
            <p data-testid="brief-error" className="text-[11px] text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
              {briefError}
            </p>
          </div>
        </div>
      )}

      {/* Production start card.
          - In briefMode (logged-in client, status='new'), the card is hidden
            entirely until the AI assistant flips readiness_status='ready'.
            That keeps the screen conversational and free of size pickers
            until the brief is complete.
          - For anonymous/legacy flows the existing trigger (>2 messages)
            is preserved. */}
      {project.status === 'new' && (briefMode ? briefReady : project.messages.length > 2) && (
        <div className="px-4 py-2 shrink-0">
          <div data-testid="start-production-card" className="max-w-3xl mx-auto border border-gray-200/60 rounded-2xl bg-white/40 backdrop-blur-sm p-3">
            {/* Brief summary card — visible only when ready. */}
            {briefMode && briefReady && brief?.summary_for_manager && (
              <div data-testid="brief-summary-card" className="mb-3 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span className="text-[11px] uppercase tracking-[0.12em] text-emerald-700 font-medium">
                    Brief ready
                  </span>
                </div>
                <pre className="text-[12px] text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">
                  {brief.summary_for_manager}
                </pre>
              </div>
            )}

            {videoSizes.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-gray-400">
                    Choose video size
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Balance: <span className="tabular-nums font-medium text-gray-700">{credits}</span>
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {videoSizes.map((s) => {
                    const active = selectedSize === s.id;
                    const range = s.min_seconds && s.max_seconds ? `${s.min_seconds}–${s.max_seconds}s` : '';
                    return (
                      <button
                        key={s.id}
                        data-testid={`size-${s.id}`}
                        onClick={() => setSelectedSize(s.id)}
                        className={`text-left px-3 py-2 rounded-xl border transition-all ${
                          active
                            ? 'border-gray-900 bg-gray-900/[0.04]'
                            : 'border-gray-200 bg-white/50 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-xs font-medium text-gray-900 block">{s.label}</span>
                        <span className="text-[10px] text-gray-400 block">{range}</span>
                        <span className="text-[10px] text-emerald-600 tabular-nums">{s.credits_required} cr.</span>
                      </button>
                    );
                  })}
                </div>
                <Button
                  data-testid="confirm-project-btn"
                  onClick={handleConfirmProject}
                  disabled={startBusy || !canAfford}
                  className="w-full mt-3 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-xl h-11 transition-all active:scale-[0.98] shadow-sm disabled:opacity-50"
                >
                  {startBusy
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <PlayCircle className="w-4 h-4 mr-2" />}
                  {briefMode
                    ? `Confirm & Start Production ${sizeCfg ? `(−${sizeCfg.credits_required} cr.)` : ''}`
                    : `Iniciar producción ${sizeCfg ? `(−${sizeCfg.credits_required} créd.)` : ''}`}
                </Button>
                {!canAfford && sizeCfg && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-2">
                    {briefMode
                      ? `You need ${sizeCfg.credits_required - credits} more credit(s). Top up from the credits panel.`
                      : `Te faltan ${sizeCfg.credits_required - credits} crédito(s). Compra más desde el panel de créditos.`}
                  </p>
                )}
                {startError && (
                  <p data-testid="start-error" className="text-[11px] text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 mt-2">
                    {startError}
                  </p>
                )}
              </>
            ) : (
              // Anonymous / preview — fall back to legacy single button.
              <Button
                data-testid="confirm-project-btn"
                onClick={handleConfirmProject}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-xl h-11 transition-all active:scale-[0.98] shadow-sm"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Confirm & Start Project
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Floating input */}
      <div className="shrink-0 px-4 pb-[env(safe-area-inset-bottom,6px)] pt-1.5 pointer-events-none">
        <div className="max-w-3xl mx-auto pointer-events-auto">
          <div
            className={`glass-pill flex flex-col rounded-[28px] px-2 py-1.5 transition-all duration-200 ${
              voice.isActive ? 'ring-1 ring-emerald-400/40 shadow-[0_4px_24px_rgba(16,185,129,0.18)]' : ''
            }`}
          >
            {voice.isActive ? (
              <RecordingBar
                testIdPrefix="chat-voice"
                phase={voice.isUploading ? 'uploading' : 'recording'}
                levels={voice.levels}
                seconds={voice.seconds}
                maxSeconds={voice.maxSeconds}
                onConfirm={voice.confirm}
                onCancel={voice.cancel}
              />
            ) : (
              <>
                {pendingFiles.length > 0 && (
                  <div data-testid="pending-files" className="flex flex-wrap gap-x-4 gap-y-1 px-3 pt-1.5 pb-0.5">
                    {pendingFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
                        {briefMode && briefBusy
                          ? <Loader2 data-testid={`pending-file-uploading-${i}`} className="w-3 h-3 animate-spin text-gray-400" />
                          : <FileIcon type={f.type} />}
                        <span className="truncate max-w-[140px]">{f.name}</span>
                        <span className="text-gray-300">{f.size}</span>
                        <button
                          data-testid={`remove-pending-file-${i}`}
                          onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                          disabled={briefMode && briefBusy}
                          className="text-gray-300 hover:text-gray-500 transition-colors disabled:opacity-30"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-1">
                  <button
                    data-testid="attach-file-btn"
                    onClick={handleFileUpload}
                    className="p-2 rounded-full text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                  >
                    <Plus className="w-[18px] h-[18px]" />
                  </button>
                  <textarea
                    data-testid="chat-input"
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => {
                      setInput(e.target.value);
                      const el = e.target;
                      el.style.height = 'auto';
                      el.style.height = Math.min(el.scrollHeight, 280) + 'px';
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Describe your video or add instructions..."
                    className="flex-1 bg-transparent text-gray-900 text-[14px] resize-none outline-none px-1 py-2 leading-5 placeholder:text-gray-400 overflow-y-auto scrollbar-thin min-h-[120px]"
                    rows={5}
                    style={{ maxHeight: '280px' }}
                  />
                  <button
                    type="button"
                    onClick={voice.start}
                    disabled={voice.isRequesting}
                    data-testid="chat-voice-mic-btn"
                    title="Hablar (transcribir con IA)"
                    aria-label="Hablar (transcribir con IA)"
                    className="p-2 rounded-full text-gray-400 hover:text-gray-700 transition-colors shrink-0 active:scale-[0.95] disabled:opacity-50"
                  >
                    <Mic className="w-[16px] h-[16px]" />
                  </button>
                  <button
                    data-testid="send-message-btn"
                    onClick={handleSend}
                    disabled={!canSend || (briefMode && briefBusy)}
                    className={`p-2 rounded-full transition-all duration-200 shrink-0 ${canSend && !(briefMode && briefBusy) ? 'text-emerald-500 hover:text-emerald-600' : 'text-gray-300 opacity-40 cursor-not-allowed'}`}
                  >
                    {briefMode && briefBusy
                      ? <Loader2 data-testid="send-uploading-spinner" className="w-7 h-7 animate-spin" />
                      : <RocketIcon className="w-7 h-7" />}
                  </button>
                </div>
              </>
            )}
          </div>
          {voice.notice && (
            <div
              data-testid="chat-voice-notice"
              className="mt-1.5 text-center text-[11px] text-amber-600 animate-fade-in"
            >
              {voice.notice}
            </div>
          )}
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
        capture={undefined}
        onChange={handleFilesSelected}
        className="hidden"
        data-testid="file-input-native"
      />
    </div>
  );
}
